import Assets from 'assets/assets-tab-icon.svg?react'
import Inspections from 'assets/inspections-tab-icon.svg?react'
import { PARTS_DATABASE_PERMISSIONS_LIST } from 'features/partsDatabase'
import {
  AlertTriangle,
  Bell,
  Blocks,
  Bot,
  CalendarRange,
  Camera,
  ChartGantt,
  ChartLine,
  ChartPie,
  ChartScatter,
  ClipboardList,
  DollarSign,
  FileText,
  GitBranch,
  Heart,
  Home,
  ImportIcon,
  Mail,
  Map,
  Network,
  Notebook,
  Ship,
  Workflow,
  Wrench,
} from 'lucide-react'
import React from 'react'
import type { FileRouteTypes } from 'routeTree.gen'
import styled, { css } from 'styled-components'
import {
  getOrgEditPermission,
  PERMISSION_CANTILEVER_ANOMALY_DETECTION_EDITOR,
  PERMISSION_CANTILEVER_ASSET_OVERVIEW_ADMIN,
  PERMISSION_CANTILEVER_BUDGETING_EDITOR,
  PERMISSION_CANTILEVER_BUDGETING_VIEWER,
  PERMISSION_CANTILEVER_DOCUMENT_OCR_ADMIN,
  PERMISSION_CANTILEVER_DOCUMENT_OCR_EDITOR,
  PERMISSION_CANTILEVER_DOCUMENT_OCR_VIEWER,
  PERMISSION_CANTILEVER_EXPLORER_EDITOR,
  PERMISSION_CANTILEVER_EXPLORER_VIEWER,
  PERMISSION_CANTILEVER_HEALTH_PLAN_EDITOR,
  PERMISSION_CANTILEVER_HEALTH_PLAN_VIEWER,
  PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_ADMIN,
  PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_EDITOR,
  PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_EDITOR,
  PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_VIEWER,
  PERMISSION_CANTILEVER_INVENTORY_MANAGEMENT_ADMIN,
  PERMISSION_CANTILEVER_INVENTORY_MANAGEMENT_EDITOR,
  PERMISSION_CANTILEVER_INVENTORY_MANAGEMENT_VIEWER,
  PERMISSION_CANTILEVER_PROCESS_FLOW_ADMIN,
  PERMISSION_CANTILEVER_SYSTEM_VIEWER,
  PERMISSION_CANTILEVER_TIME_SERIES_EDITOR,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PERMISSION_MATTERPORT_WALKTHROUGH_VIEWER,
  RolodexPermissionsValues,
} from 'types'

import { SelectedTab } from './types'

export interface RouteRequiringSiteID {
  basePath: string
  targetPath: string
  tabKey: SelectedTab
}

const FilledIcon = css`
  height: 1.25rem;
  width: 1.25rem;

  path {
    fill: ${({ theme }) => theme.forecast.tertiaryText};
  }

  &:active {
    path {
      fill: ${({ theme }) => theme.forecast.secondaryText};
    }
  }
`

const OutlinedIcon = css`
  height: 1.25rem;
  width: 1.25rem;

  path {
    background-color: ${({ theme }) => theme.forecast.tertiaryText};
  }

  &:active {
    path {
      background-color: ${({ theme }) => theme.forecast.secondaryText};
    }
  }
`

const AssetsIcon = styled(Assets)`
  ${FilledIcon}
`

const InspectionsIcon = styled(Inspections)`
  ${FilledIcon}
`

const HeartIcon = styled(Heart)`
  ${OutlinedIcon}
`

const HomeIcon = styled(Home)`
  ${OutlinedIcon}
`

const MailIcon = styled(Mail)`
  ${OutlinedIcon}
`

const MapIcon = styled(Map)`
  ${OutlinedIcon}
`

const CalendarIcon = styled(CalendarRange)`
  ${FilledIcon}
`

const ChartLineIcon = styled(ChartLine)`
  ${OutlinedIcon}
`

const ClipboardIcon = styled(ClipboardList)`
  ${OutlinedIcon}
`

const PieChartIcon = styled(ChartPie)`
  ${OutlinedIcon}
`

const BarChartIcon = styled(ChartGantt)`
  ${OutlinedIcon}
`

const DashboardIcon = styled(ChartGantt)`
  ${OutlinedIcon}
`

const RobotIcon = styled(Bot)`
  ${FilledIcon}
`

const PartsIcon = styled(Blocks)`
  ${OutlinedIcon}
`

const WrenchIcon = styled(Wrench)`
  ${OutlinedIcon}
`

const NetworkIcon = styled(Network)`
  ${OutlinedIcon}
`

const ProcessFlowIcon = styled(GitBranch)`
  ${OutlinedIcon}
`

const DocIcon = styled(FileText)`
  ${OutlinedIcon}
`

const CasesIcon = styled(Notebook)`
  ${OutlinedIcon}
`

const ReportingIcon = styled(FileText)`
  ${OutlinedIcon}
`

const BudgetingIcon = styled(DollarSign)`
  ${OutlinedIcon}
`

const ImportWizardIcon = styled(ImportIcon)`
  ${OutlinedIcon}
`

const ProcessDataIcon = styled(ChartScatter)`
  ${OutlinedIcon}
`
const MatterportWalkthroughIcon = styled(Camera)`
  ${OutlinedIcon}
`
const ShipsIcon = styled(Ship)`
  ${OutlinedIcon}
`

const AnomalyDetectionIcon = styled(AlertTriangle)`
  ${OutlinedIcon}
`

const AlertsIcon = styled(Bell)`
  ${OutlinedIcon}
`

const WorkflowIcon = styled(Workflow)`
  ${OutlinedIcon}
`

const US_NAVY_ORG_PERMISSION = getOrgEditPermission('us-navy-69a72a')

export interface NavItemConfig {
  to: FileRouteTypes['to']
  params?: (siteID: string, orgID?: string) => Record<string, string>
  icon: React.ComponentType
  label: string
  permissions?: RolodexPermissionsValues[]
  except?: RolodexPermissionsValues[]
  shouldShow?: (context: {
    siteID: string
    canVisitSiteView: boolean
  }) => boolean
}

export const NAV_ITEMS_CONFIG: Partial<Record<SelectedTab, NavItemConfig>> = {
  Home: {
    to: '/home/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: HomeIcon,
    label: 'Home',
  },
  Overview: {
    to: '/overview/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: MapIcon,
    label: 'Site Overview',
    shouldShow: ({ canVisitSiteView }) => canVisitSiteView,
  },
  'Production Dashboard': {
    to: '/production-dashboard/$orgID/$siteID',
    params: (siteID, orgID) => ({ orgID: orgID ?? '', siteID: siteID ?? '' }),
    icon: DashboardIcon,
    label: 'Production Dashboard',
  },
  Inbox: {
    to: '/inbox/$orgOrSiteID',
    params: (siteID, orgID) => ({ orgOrSiteID: siteID ?? orgID ?? '' }),
    icon: MailIcon,
    label: 'Inbox',
  },
  Cases: {
    to: '/cases/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: CasesIcon,
    label: 'Cases',
  },
  Alerts: {
    to: '/alerts/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: AlertsIcon,
    label: 'Alerts',
    // TODO: Re-enable permissions for alerts viewing after dev
    // permissions: [
    //   PERMISSION_CANTILEVER_ALERTS_VIEWER,
    //   PERMISSION_CANTILEVER_ALERTS_EDITOR,
    // ],
  },
  Assets: {
    to: '/assets/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: AssetsIcon,
    label: 'Assets',
  },
  Ships: {
    to: '/ships/$siteID',
    params: (siteID) => ({ siteID }),
    icon: ShipsIcon,
    label: 'Ships',
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  Inspections: {
    to: '/inspections/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: InspectionsIcon,
    label: 'Inspections',
  },
  'Fleet Analysis': {
    to: '/fleet-analysis/$siteID',
    params: (siteID) => ({ siteID }),
    icon: ChartLineIcon,
    label: 'Fleet Analysis',
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  'Inspection Scheduling': {
    to: '/scheduling/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: CalendarIcon,
    label: 'Inspection Scheduling',
    permissions: [
      PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_EDITOR,
      PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  'Availability Planning': {
    to: '/availability-planning/$siteID',
    params: (siteID) => ({ siteID }),
    icon: CalendarIcon,
    label: 'Availability Planning',
    permissions: [US_NAVY_ORG_PERMISSION, PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  'Health Plans': {
    to: '/health-plans/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: HeartIcon,
    label: 'Health Plans',
    permissions: [
      PERMISSION_CANTILEVER_HEALTH_PLAN_VIEWER,
      PERMISSION_CANTILEVER_HEALTH_PLAN_EDITOR,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  Budgeting: {
    to: '/budgeting/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: BudgetingIcon,
    label: 'Budgeting',
    permissions: [
      PERMISSION_CANTILEVER_BUDGETING_EDITOR,
      PERMISSION_CANTILEVER_BUDGETING_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
  },
  Walkdowns: {
    to: '/walkdowns/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: ClipboardIcon,
    label: 'Walkdowns',
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
    except: [US_NAVY_ORG_PERMISSION],
  },
  Reporting: {
    to: '/reporting/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: ReportingIcon,
    label: 'Reporting',
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  Maximo: {
    to: '/maximo/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: WrenchIcon,
    label: 'Maximo',
    shouldShow: ({ siteID }) =>
      siteID === 'naes-longridge' || siteID === 'longridge-ac0a01',
  },
  'System Lens': {
    to: '/system-lens/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: NetworkIcon,
    label: 'System Lens',
    permissions: [
      PERMISSION_CANTILEVER_SYSTEM_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
  },
  'Point Cloud': {
    to: '/point-cloud/$siteID',
    params: (siteID) => ({ siteID }),
    icon: MapIcon,
    label: '3D View',
    permissions: [
      PERMISSION_CANTILEVER_SYSTEM_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  Walkthrough: {
    to: '/walkthrough/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: MatterportWalkthroughIcon,
    label: 'Walkthrough',
    permissions: [
      PERMISSION_MATTERPORT_WALKTHROUGH_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  Explorer: {
    to: '/explorer/$orgOrSiteID',
    params: (siteID, orgID) => ({ orgOrSiteID: siteID ?? orgID ?? '' }),
    icon: PieChartIcon,
    label: 'Explorer',
    permissions: [
      PERMISSION_CANTILEVER_EXPLORER_VIEWER,
      PERMISSION_CANTILEVER_EXPLORER_EDITOR,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
  },
  'Parts Thread': {
    to: '/parts/$siteID',
    params: (siteID) => ({ siteID }),
    icon: PartsIcon,
    label: 'Parts Thread',
    permissions: PARTS_DATABASE_PERMISSIONS_LIST,
    except: [US_NAVY_ORG_PERMISSION],
  },
  'Process Flow Diagrams': {
    to: '/process-flows/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: ProcessFlowIcon,
    label: 'Process Flow Diagrams',
    permissions: [PERMISSION_CANTILEVER_PROCESS_FLOW_ADMIN],
  },
  'Value Stories': {
    to: '/value-stories/$siteID',
    params: (siteID) => ({ siteID }),
    icon: DocIcon,
    label: 'Value Stories',
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  'Jobs Overview': {
    to: '/jobs',
    icon: BarChartIcon,
    label: 'Jobs Overview',
    permissions: [
      PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_ADMIN,
      PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_EDITOR,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  'Inventory Management': {
    to: '/inventory',
    icon: RobotIcon,
    label: 'Inventory Management',
    permissions: [
      PERMISSION_CANTILEVER_INVENTORY_MANAGEMENT_ADMIN,
      PERMISSION_CANTILEVER_INVENTORY_MANAGEMENT_EDITOR,
      PERMISSION_CANTILEVER_INVENTORY_MANAGEMENT_VIEWER,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  'Import Wizard': {
    to: '/import-wizard/$siteID',
    params: (siteID) => ({ siteID }),
    icon: ImportWizardIcon,
    label: 'Import Wizard',
    permissions: [PERMISSION_CANTILEVER_ASSET_OVERVIEW_ADMIN],
  },
  'Process Data': {
    to: '/process-data/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: ProcessDataIcon,
    label: 'Process Data',
    permissions: [PERMISSION_CANTILEVER_TIME_SERIES_EDITOR],
  },
  'Anomaly Detection': {
    to: '/anomaly-detection/$siteID',
    params: (siteID) => ({ siteID }),
    icon: AnomalyDetectionIcon,
    label: 'Anomaly Detection',
    permissions: [PERMISSION_CANTILEVER_ANOMALY_DETECTION_EDITOR],
  },
  Documents: {
    to: '/documents/$siteID',
    params: (siteID) => ({ siteID }),
    icon: DocIcon,
    label: 'Documents',
    permissions: [
      PERMISSION_CANTILEVER_DOCUMENT_OCR_ADMIN,
      PERMISSION_CANTILEVER_DOCUMENT_OCR_VIEWER,
      PERMISSION_CANTILEVER_DOCUMENT_OCR_EDITOR,
    ],
  },
  Libra: {
    to: '/libra/$orgID',
    params: (siteID, orgID) => ({ orgID: orgID ?? '' }),
    icon: WorkflowIcon,
    label: 'Libra',
  },
  'NII Dashboard': {
    to: '/nii-dashboard/$orgID',
    params: (siteID, orgID) => ({ orgID: orgID ?? '' }),
    icon: DashboardIcon,
    label: 'NII Dashboard',
  },
  'Virtual Reports': {
    to: '/virtual-reports/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: DocIcon,
    label: 'Virtual Reports',
  },
}

const SITE_ID_ROUTE_EXCEPTION = [SelectedTab.Explorer]

/**
 * Extracts all routes from NAV_ITEMS_CONFIG that require a siteID parameter
 * Returns an array of route information for generalized redirect logic
 */
export function getRoutesRequiringSiteID(): ReadonlyArray<RouteRequiringSiteID> {
  return Object.entries(NAV_ITEMS_CONFIG)
    .filter(
      (item) =>
        item[1]?.to &&
        item[1]?.to.includes('$siteID') &&
        !SITE_ID_ROUTE_EXCEPTION.includes(item[0] as SelectedTab)
    )
    .map(([tabKey, config]) => {
      const basePath = config.to.replace(/\/\{-?\$siteID\}.*$/, '')
      const targetPath = basePath.substring(1) // Remove leading slash for navigation
      return { basePath, targetPath, tabKey: tabKey as SelectedTab }
    })
}
